import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  SkuMappingImportItem,
  SkuMappingImportResponse,
  SkuMappingsSearchArgs,
  SkuMappingsSearchResponse,
} from './types';

/**
 * Pure request builders — exported so the exact wire (URL + params + body) is
 * lockable in a `toEqual` unit test. `fetchBaseQuery` can't be intercepted, so
 * this is how we prove the request matches the live capture verbatim.
 *
 * Live captures (portal.dev super-admin, 2026-06-01):
 *   GET /sku/v1/mappings/search?page=1&pageSize=10&sortBy=extSku&sortAsc=true&system=legacy
 *   DELETE /sku/v1/mappings/{id}
 *   POST /sku/v1/mappings/import  { items: [...] }
 */
export const skuMappingsRequestBuilders = {
  searchSkuMappings: ({
    searchString,
    page = 1,
    pageSize = 10,
    sortBy = 'extSku',
    sortDir = 'asc',
  }: SkuMappingsSearchArgs) => {
    // Param order matches the live request exactly:
    // page, pageSize, sortBy, sortAsc, system, [searchAll].
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    qs.set('sortBy', sortBy);
    qs.set('sortAsc', String(sortDir === 'asc'));
    // Only "legacy" is wired today — the legacy service hard-codes it too.
    qs.set('system', 'legacy');
    if (searchString) qs.set('searchAll', searchString);
    return { url: `${services.sku}/v1/mappings/search?${qs.toString()}`, method: 'GET' as const };
  },
  deleteSkuMapping: (id: string) => ({
    url: `${services.sku}/v1/mappings/${encodeURIComponent(id)}`,
    method: 'DELETE' as const,
  }),
  importSkuMappings: (items: SkuMappingImportItem[]) => ({
    url: `${services.sku}/v1/mappings/import`,
    method: 'POST' as const,
    body: { items },
  }),
};

/** Unwrap the search envelope. Live returns `{ mappings, total }`; tolerate a
 * `{ data: {...} }` wrapper and a bare array defensively (other portal
 * services have shipped both). */
export function unwrapSkuMappings(
  response: SkuMappingsSearchResponse | { data?: SkuMappingsSearchResponse } | unknown,
): SkuMappingsSearchResponse {
  const r = response as SkuMappingsSearchResponse & { data?: SkuMappingsSearchResponse };
  if (r?.data && Array.isArray(r.data.mappings)) return r.data;
  if (Array.isArray(r?.mappings)) return { mappings: r.mappings, total: r.total ?? r.mappings.length };
  return { mappings: [], total: 0 };
}

/**
 * SKU-mapping slice (super-admin only surface). Mirrors the legacy
 * `skuMapping` saga/service: paginated search, single delete, bulk import.
 */
export const skuMappingsApi = createApi({
  reducerPath: 'skuMappingsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['SkuMapping'],
  endpoints: (builder) => ({
    searchSkuMappings: builder.query<SkuMappingsSearchResponse, SkuMappingsSearchArgs>({
      query: skuMappingsRequestBuilders.searchSkuMappings,
      transformResponse: unwrapSkuMappings,
      providesTags: [{ type: 'SkuMapping', id: 'LIST' }],
    }),
    deleteSkuMapping: builder.mutation<void, { id: string }>({
      query: ({ id }) => skuMappingsRequestBuilders.deleteSkuMapping(id),
      invalidatesTags: [{ type: 'SkuMapping', id: 'LIST' }],
    }),
    importSkuMappings: builder.mutation<SkuMappingImportResponse, { items: SkuMappingImportItem[] }>({
      query: ({ items }) => skuMappingsRequestBuilders.importSkuMappings(items),
      invalidatesTags: [{ type: 'SkuMapping', id: 'LIST' }],
    }),
  }),
});

export const {
  useSearchSkuMappingsQuery,
  useDeleteSkuMappingMutation,
  useImportSkuMappingsMutation,
} = skuMappingsApi;
